import { useGetAllEmployeeGroups, useGetAllPersons, useGetPersonsEmployeeGroupSummary } from '@hr/api/endpoints'
import { LoadingContainer } from '@hr/common/loaders/loadingcontainer'
import { LabelMeter } from '@hr/common/meter/LabelMeter'
import { average, localized, median } from '@hr/common/utils'
import { useTranslation } from 'react-i18next'
import styles from './Card.module.css'

// parameter: person.salaryDetails object
export function determineSalary({ formOfSalary, monthlyWage, hourlyWageATP, hourlyWageOTP, hourlyWagePTP }) {
	if (formOfSalary === 1) return monthlyWage

	const hourly = hourlyWageATP || hourlyWageOTP || hourlyWagePTP
	if (hourly) return hourly * 7.5 * 21.5 // avg daily hours * avg monthly working days
}

export function SalaryLevelsGrouped() {
	const { t } = useTranslation()

	const { data: groups } = useGetAllEmployeeGroups() // determine active employee group status
	const { data: groupPersons } = useGetPersonsEmployeeGroupSummary() // detemine which group a person belongs
	const { data: persons } = useGetAllPersons() // determine person employed status

	const countDistribution = () => {
		const groupMap = {}
		groups.forEach((group) => {
			if (group.active) {
				groupMap[group.id] = group
				group.women = []
				group.men = []
			}
		})

		const personGroupMap = {}
		groupPersons.forEach((person) => {
			personGroupMap[person.personId] = person.employeeGroupId
		})

		persons.forEach((person) => {
			const status = person.employmentDetails?.personStatus
			if ((status === 'EMPLOYED' || !status) && person.socSec) {
				const groupId = personGroupMap[person.personId]
				const group = groupMap[groupId]
				const { salaryDetails } = person
				if (group && salaryDetails) {
					const salary = determineSalary(salaryDetails)
					if (salary) {
						const g = (person.gender || '').trim().toUpperCase()
						if (g === 'M') group.men.push(salary)
						else if (g === 'N') group.women.push(salary)
					}
				}
			}
		})

		// Calculate averages and medians for the active employee groups
		Object.values(groupMap).forEach((group) => {
			group.menAverage = average(group.men) || 0
			group.womenAverage = average(group.women) || 0
			group.averageRatio =
				group.womenAverage + group.menAverage === 0 ? 0.5 : group.womenAverage / (group.womenAverage + group.menAverage)

			group.menMedian = median(group.men) || 0
			group.womenMedian = median(group.women) || 0
			group.medianRatio =
				group.womenMedian + group.menMedian === 0 ? 0.5 : group.womenMedian / (group.womenMedian + group.menMedian)
		})

		return (
			<>
				<h4 className={styles.cardHeader}>
					{t('SalaryLevelsGrouped', 'Palkkatasot työntekijäryhmittäin ja sukupuolittain')}
				</h4>
				<table style={{ width: '100%', marginTop: '2em' }}>
					<thead>
						<tr style={{ borderBottom: '1px solid #eee' }}>
							<th>{t('EmployeeGroup', 'Työntekijäryhmä')}</th>
							<th>{t('AverageWage', 'Keskipalkka (€/kk)')}</th>
							<th>{t('MedianWage', 'Mediaanipalkka (€/kk)')}</th>
						</tr>
					</thead>
					<tbody>
						{Object.values(groupMap).map((group) => (
							<tr key={group.id} style={{ borderBottom: '1px solid #eee' }} className="padded">
								<td>
									<span className={styles.groupBadge} style={{ background: group.colour }} title={group.groupCode}>
										{group.name}
									</span>
								</td>
								<td style={{ paddingRight: '3rem' }}>
									<LabelMeter
										leftLabel={`${localized.numberFormat.format(Math.round(group.womenAverage))} €`}
										value={group.averageRatio}
										rightLabel={`${localized.numberFormat.format(Math.round(group.menAverage))} €`}
									/>
								</td>
								<td>
									<LabelMeter
										leftLabel={`${localized.numberFormat.format(Math.round(group.womenMedian))} €`}
										value={group.medianRatio}
										rightLabel={`${localized.numberFormat.format(Math.round(group.menMedian))} €`}
									/>
								</td>
							</tr>
						))}
					</tbody>
				</table>

				<div className={styles.legend}>
					<div>
						<span style={{ background: 'var(--palette-1)' }}></span>
						{t('Women', 'Naiset')}
					</div>
					<div>
						<span style={{ background: 'var(--palette-2)' }}></span>
						{t('Men', 'Miehet')}
					</div>
				</div>
			</>
		)
	}

	return groups && groupPersons && persons ? countDistribution() : <LoadingContainer />
}
